import L from 'leaflet';

/**
 * Options for RotatingIcon
 */
export interface RotatingIconOptions extends L.DivIconOptions {
  /**
   * Initial rotation angle in degrees
   */
  rotationAngle?: number;

  /**
   * Offset angle to apply to all rotations
   */
  rotationOffset?: number;

  /**
   * Translation values for positioning the icon
   * Values are in percentage (e.g. [-50, -50] centers the icon)
   */
  translationOffset?: [number, number];
}

/**
 * A DivIcon extension that supports rotation
 */
export class RotatingIcon extends L.DivIcon {
  private div: HTMLElement | null = null;
  private _rotationAngle: number = 0;
  private _rotationOffset: number = 0;
  private _translateValues: [number, number] = [-50, -50]; // Default to center

  constructor(options?: RotatingIconOptions) {
    super(options);
    if (options?.rotationAngle !== undefined) {
      this._rotationAngle = options.rotationAngle;
    }
    if (options?.rotationOffset !== undefined) {
      this._rotationOffset = options.rotationOffset;
    }
    if (options?.translationOffset !== undefined) {
      this._translateValues = options.translationOffset;
    }
    this.createIcon();
  }

  createIcon(oldIcon?: HTMLElement): HTMLElement {
    const outerDiv = document.createElement('div');
    this.div = document.createElement('div');

    if (this.options.className) {
      this.div.className = this.options.className;
    }

    if (this.options.iconUrl) {
      this.div.style.backgroundImage = `url(${this.options.iconUrl})`;
      this.div.style.backgroundSize = 'contain';
      this.div.style.backgroundRepeat = 'no-repeat';
      this.div.style.backgroundPosition = 'center';
    }

    if (this.options.iconSize) {
      const dimensions = this.options.iconSize as [number, number];
      this.div.style.width = `${dimensions[0]}px`;
      this.div.style.height = `${dimensions[1]}px`;
    }

    this.updateTransform();

    outerDiv.appendChild(this.div);
    return outerDiv;
  }

  /**
   * Rotates the icon to the specified angle
   * @param deg Rotation angle in degrees
   */
  rotate(deg: number): void {
    this._rotationAngle = deg;
    if (this.div) {
      this.updateTransform();
    }
  }

  /**
   * Gets the current rotation angle (without offset)
   * @returns The rotation angle in degrees
   */
  getRotation(): number {
    return this._rotationAngle;
  }

  /**
   * Sets the rotation offset
   * @param offset The rotation offset in degrees
   */
  setRotationOffset(offset: number): void {
    this._rotationOffset = offset;
    if (this.div) {
      this.updateTransform();
    }
  }

  /**
   * Updates the transform property with the current translation and rotation
   */
  private updateTransform(): void {
    if (this.div) {
      const translateX = this._translateValues[0];
      const translateY = this._translateValues[1];
      const rotation = this._rotationAngle + this._rotationOffset;

      this.div.style.transform = `translate(${translateX}%, ${translateY}%) rotate(${rotation}deg)`;
    }
  }
}
